import { createRoot } from "react-dom/client";
import { ConvexProvider, ConvexReactClient } from "convex/react";
import App from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { logger } from "./lib/logger";
import "./index.css";

// Enable React DevTools in development
if (import.meta.env.DEV) {
  // React DevTools will auto-detect React in development mode
  // Expose logger to window for debugging
  logger.info("Development mode enabled - React DevTools available");
  logger.info("Access logger via: window.logger");
}

// Get Convex URL from env or use fallback
let convexUrl = import.meta.env.VITE_CONVEX_URL as string;

// Fallback to default deployment if not set or if it's a deployment key format
if (!convexUrl || convexUrl.startsWith("dev:")) {
  // Convert deployment key format to URL if needed
  if (convexUrl?.startsWith("dev:")) {
    const deploymentMatch = convexUrl.match(/dev:([^|]+)/);
    if (deploymentMatch) {
      convexUrl = `https://${deploymentMatch[1]}.convex.cloud`;
      logger.info(`Converted deployment key to URL: ${convexUrl}`);
    }
  }
  
  // Use fallback if still not set
  if (!convexUrl) {
    convexUrl = "https://first-coyote-326.convex.cloud";
    logger.warn(`Using fallback Convex URL: ${convexUrl}`);
  }
}

logger.info(`Connecting to Convex: ${convexUrl}`);

const convex = new ConvexReactClient(convexUrl);

// Log Convex connection status with helpful error messages
convex.onConnectionChange((isConnected) => {
  if (isConnected) {
    logger.info("✅ Connected to Convex backend");
  } else {
    logger.warn("⚠️  Disconnected from Convex backend");
    if (import.meta.env.DEV) {
      logger.info("💡 Tip: Make sure Convex dev server is running: npx convex dev");
      logger.info(`   Using Convex URL: ${convexUrl}`);
      logger.info("   If Convex isn't running, the app will use the fallback URL");
    }
  }
});

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <ConvexProvider client={convex}>
      <App />
    </ConvexProvider>
  </ErrorBoundary>
);
